import { ReactNode } from 'react';
import { EXPIRY_PRESETS, presetExpiry } from './promoFormModel';

const HINT_STYLE = { fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' };

/** Label + control + hint + inline error, wired for screen readers (ProviderForm conventions). */
export function FormField({ id, label, hint, error, children }: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="admin-form-group">
      <label className="admin-form-label" htmlFor={id}>{label}</label>
      {children}
      {hint && <div id={`${id}-hint`} style={HINT_STYLE}>{hint}</div>}
      {error && (
        <div id={`${id}-error`} role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}

/** a11y props for an input rendered inside `FormField`. */
export function describedBy(id: string, error?: string, hint?: boolean) {
  const ids = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ');
  return { 'aria-invalid': !!error, 'aria-describedby': ids || undefined };
}

export function RadioGroup<T extends string>({ name, label, value, options, disabled, onChange }: {
  name: string;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <div className="admin-form-group">
      <span className="admin-form-label" id={`${name}-label`}>{label}</span>
      <div role="radiogroup" aria-labelledby={`${name}-label`} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {options.map((o) => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="radio" name={name} checked={value === o.value} disabled={disabled} onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/** D-9 — optional expiry: a date/time, a 7/30/90-day preset from the start, or "No expiry". */
export function ExpiryField({ startsAt, expiresAt, noExpiry, disabled, error, onChange }: {
  startsAt: string;
  expiresAt: string;
  noExpiry: boolean;
  disabled?: boolean;
  error?: string;
  onChange: (next: { expiresAt: string; noExpiry: boolean }) => void;
}) {
  return (
    <FormField id="promo-expires" label="Expiry (Asia/Dubai)" error={error}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          id="promo-expires"
          type="datetime-local"
          className="admin-form-input"
          style={{ maxWidth: '14rem' }}
          value={noExpiry ? '' : expiresAt}
          disabled={disabled || noExpiry}
          {...describedBy('promo-expires', error)}
          onChange={(e) => onChange({ expiresAt: e.target.value, noExpiry: false })}
        />
        {EXPIRY_PRESETS.map((days) => (
          <button
            key={days}
            type="button"
            className="admin-btn admin-btn-outline admin-btn-sm"
            disabled={disabled}
            onClick={() => onChange({ expiresAt: presetExpiry(startsAt, days), noExpiry: false })}
          >
            {days} days
          </button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={noExpiry}
            disabled={disabled}
            onChange={(e) => onChange({ expiresAt, noExpiry: e.target.checked })}
          />
          No expiry
        </label>
      </div>
    </FormField>
  );
}
